import type { IntlShape, MessageDescriptor } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';
import React from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { markErrorAsHandled } from '@confluence/graphql';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker';
import {
	ADMIN_ANNOUNCEMENT_BANNER_PUBLISH_EXPERIENCE,
	ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
} from '@confluence/experience-tracker';
import type { FlagsStateContainer } from '@confluence/flags';
import { ADMIN_ANNOUNCEMENT_BANNER_RENDERER } from '@confluence/named-routes';

import { shouldMarkErrorAsHandled } from './shouldMarkErrorAsHandled';
import {
	MutationStatus,
	getMutationAnalyticsEventPayload,
} from './getMutationAnalyticsEventPayload';
import type { UpdateAdminAnnouncementBannerMutation as UpdateAdminAnnouncementBannerMutationType } from './queries/__types__/UpdateAdminAnnouncementBannerMutation';
import type {
	CreateAdminAnnouncementBannerMutation as CreateAdminAnnouncementBannerMutationType,
	CreateAdminAnnouncementBannerMutation_createAdminAnnouncementBanner_errors_extensions_AdminAnnouncementBannerMutationErrorExtension_adminAnnouncementBannerList as ConflictingBannerType,
} from './queries/__types__/CreateAdminAnnouncementBannerMutation';
import type { MutationSource } from './AABForm/AABFormConstants';
import { i18n as sharedI18n, MutationType, MutationUpdateReason } from './AABForm/AABFormConstants';

const mutationTypeMap: Record<MutationType, string> = {
	[MutationType.CREATE]: 'Create',
	[MutationType.UPDATE]: 'Update',
};

const mutationTypeMapM2: Record<MutationUpdateReason, MutationType> = {
	[MutationUpdateReason.PUBLISH]: MutationType.CREATE,
	[MutationUpdateReason.UPDATE]: MutationType.UPDATE,
	[MutationUpdateReason.ARCHIVE]: MutationType.UPDATE,
	// REQUIRED that all enums are addressed, but MutationUpdateReason.TURNOFF should
	// not be used and will be obsolete
	[MutationUpdateReason.TURNOFF]: MutationType.UPDATE,
};

const i18nM1 = defineMessages({
	publishErrorFlagTitle: {
		id: 'admin-announcement-banner.flag.error.title',
		defaultMessage: 'We had an issue publishing this announcement',
		description: 'Title for Flag that indicates the banner publish failed',
	},
	errorFlagDescription: {
		id: 'admin-announcement-banner.flag.error.description',
		defaultMessage: 'Refresh the page and try again.',
		description: 'Description for Flag that indicates the banner publish failed',
	},
	updateErrorFlagTitle: {
		id: 'admin-announcement-banner.update.error.flag.title',
		defaultMessage: 'We had an issue updating this announcement',
		description: 'The title of the flag component when the update fails',
	},
	turnOffErrorFlagTitle: {
		id: 'admin-announcement-banner.turn-off.error.flag.title',
		defaultMessage: 'We had an issue turning off this announcement',
		description: 'The text to show in the flag title when turning off the banner fails',
	},
	genericErrorFlagDescription: {
		id: 'admin-announcement-banner.error.flag.description',
		defaultMessage: 'Refresh the page and try again.',
		description:
			'Description for Flag that indicates there has been an issue publishing or updating their form.',
	},
});

const i18nM2 = defineMessages({
	publishErrorFlagTitle: {
		id: 'admin-announcement-banner.publish.error.flag.title.m2',
		defaultMessage: 'We had an issue publishing {announcementTitle}',
		description: 'Title for Flag that indicates the banner publish failed.',
	},
	updateErrorFlagTitle: {
		id: 'admin-announcement-banner.update.error.flag.title.m2',
		defaultMessage: 'We had an issue updating {announcementTitle}',
		description: 'The title of the flag component when the update failed.',
	},
	archiveErrorFlagTitle: {
		id: 'admin-announcement-banner.archiving.error.flag.title',
		defaultMessage: 'We had an issue archiving {announcementTitle}',
		description: 'Title for Flag that indicates the banner archival failed.',
	},
	scheduleErrorFlagTitle: {
		id: 'admin-announcement-banner.schedule.error.flag.description',
		defaultMessage: 'We had an issue scheduling {announcementTitle}',
		description:
			'Title for Flag that indicates banner failed to publish or update due to scheduling issues.',
	},
	restrictedPublishFlagTitle: {
		id: 'admin-announcement-banner.restricted.publish.flag.title',
		defaultMessage: 'Could not publish {announcementTitle}',
		description: 'Title for Flag that indicates the banner publish failed.',
	},
	restrictedPublishFlagDescription: {
		id: 'admin-announcement-banner.restricted.publish.flag.description',
		defaultMessage: '<a>{announcementTitle}</a> is currently active.',
		description:
			'Description for Flag to convey that a new banner cannot be published while there is an active banner.',
	},
	genericArchiveErrorFlagTitle: {
		id: 'admin-announcement-banner.archiving.generic.error.flag.title',
		defaultMessage: 'We had an issue archiving this announcement',
		description: 'Title for Flag that indicates the banner archival failed.',
	},
});

type SchedulingErrorDetails = {
	submittedBannerTitle: string;
	hasConflictWithActiveBanner: boolean;
	conflictingActiveBanner?: ConflictingBannerType;
};

// M1 Error handling
type HandleErrorsProps = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	experienceTracker: ExperienceTrackerAPI;
	flags: FlagsStateContainer;
	formatMessage: IntlShape['formatMessage'];
	isExpectedError: boolean;
	mutationType: MutationType;
	data?: UpdateAdminAnnouncementBannerMutationType | CreateAdminAnnouncementBannerMutationType;
	mutationUpdateReason?: MutationUpdateReason;
	unexpectedError?: ApolloError;
};

// M2 Error handling
type HandleErrorsM2Props = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	experienceTracker: ExperienceTrackerAPI;
	flags: FlagsStateContainer;
	formatMessage: IntlShape['formatMessage'];
	isExpectedError?: boolean;
	data: UpdateAdminAnnouncementBannerMutationType | CreateAdminAnnouncementBannerMutationType;
	mutationUpdateReason: MutationUpdateReason;
	mutationSource: MutationSource;
	schedulingErrorDetails?: SchedulingErrorDetails;
};

// M2 Error handling, unexpected errors
type HandleUnexpectedMutationErrorsProps = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	experienceTracker: ExperienceTrackerAPI;
	flags: FlagsStateContainer;
	formatMessage: IntlShape['formatMessage'];
	unexpectedError?: ApolloError;
	mutationUpdateReason: MutationUpdateReason;
	mutationSource: MutationSource;
};

const getErrorTitleM1 = (
	mutationType: MutationType,
	updateReason?: MutationUpdateReason,
): MessageDescriptor => {
	if (mutationType === MutationType.CREATE || updateReason === MutationUpdateReason.PUBLISH) {
		return i18nM1.publishErrorFlagTitle;
	}
	return updateReason === MutationUpdateReason.UPDATE
		? i18nM1.updateErrorFlagTitle
		: i18nM1.turnOffErrorFlagTitle;
};

// M2, unexpected errors show generic titles
const getGenericTitleError = (updateReason: MutationUpdateReason): MessageDescriptor => {
	switch (updateReason) {
		case MutationUpdateReason.UPDATE:
			return i18nM1.updateErrorFlagTitle;
		case MutationUpdateReason.ARCHIVE:
			return i18nM2.genericArchiveErrorFlagTitle;
		default:
			return i18nM1.publishErrorFlagTitle;
	}
};

//M2, expected errors show banner titles
const getErrorTitleM2 = (
	announcementTitle: string,
	updateReason: MutationUpdateReason,
	schedulingErrorDetails?: SchedulingErrorDetails,
): [MessageDescriptor, { announcementTitle: string }?] => {
	if (schedulingErrorDetails) {
		if (schedulingErrorDetails.hasConflictWithActiveBanner) {
			return [i18nM2.restrictedPublishFlagTitle, { announcementTitle }];
		} else {
			return [i18nM2.scheduleErrorFlagTitle, { announcementTitle }];
		}
	} else {
		switch (updateReason) {
			case MutationUpdateReason.PUBLISH:
				return [i18nM2.publishErrorFlagTitle, { announcementTitle }];
			case MutationUpdateReason.UPDATE:
				return [i18nM2.updateErrorFlagTitle, { announcementTitle }];
			case MutationUpdateReason.ARCHIVE:
				return [i18nM2.archiveErrorFlagTitle, { announcementTitle }];
			case MutationUpdateReason.TURNOFF:
				return [i18nM2.archiveErrorFlagTitle, { announcementTitle }];
		}
	}
};

const getErrorDescription = (
	schedulingErrorDetails?: SchedulingErrorDetails,
): [
	MessageDescriptor,
	{
		announcementTitle: string;
		a: (str: React.ReactNode[]) => JSX.Element;
	}?,
] => {
	const bannerAri = schedulingErrorDetails?.conflictingActiveBanner?.id;
	const activeBannerEditFormUrl = ADMIN_ANNOUNCEMENT_BANNER_RENDERER.toUrl({
		id: bannerAri,
	});
	if (schedulingErrorDetails && schedulingErrorDetails.conflictingActiveBanner) {
		return [
			i18nM2.restrictedPublishFlagDescription,
			{
				announcementTitle:
					schedulingErrorDetails.conflictingActiveBanner.title ||
					sharedI18n.untitledBannerTitle.defaultMessage,
				a: (str: React.ReactNode[]) => <a href={activeBannerEditFormUrl}>{str}</a>,
			},
		];
	}
	return [i18nM1.errorFlagDescription];
};

const getProperlyTypedData = (
	isMutationTypeCreate: boolean,
	data?: CreateAdminAnnouncementBannerMutationType | UpdateAdminAnnouncementBannerMutationType,
) => {
	return isMutationTypeCreate
		? (data as CreateAdminAnnouncementBannerMutationType)?.createAdminAnnouncementBanner
		: (data as UpdateAdminAnnouncementBannerMutationType)?.updateAdminAnnouncementBanner;
};

export const handleErrors = ({
	createAnalyticsEvent,
	experienceTracker,
	flags,
	formatMessage,
	isExpectedError,
	mutationType,
	data,
	mutationUpdateReason,
	unexpectedError,
}: HandleErrorsProps) => {
	const isMutationTypeCreate = mutationType === MutationType.CREATE;
	const mutationData = getProperlyTypedData(isMutationTypeCreate, data);

	const convertedError = mutationData?.errors?.[0];

	const mutationError = {
		message:
			(isExpectedError ? convertedError?.message : unexpectedError?.message) ||
			`${mutationTypeMap[mutationType]} banner error`,
		name: `${mutationTypeMap[mutationType]} banner mutation failed`,
	};
	if (
		mutationUpdateReason === MutationUpdateReason.TURNOFF ||
		mutationUpdateReason === MutationUpdateReason.ARCHIVE
	) {
		experienceTracker.stopOnError({
			name: ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
			error: mutationError,
		});
	} else {
		experienceTracker.stopOnError({
			name: ADMIN_ANNOUNCEMENT_BANNER_PUBLISH_EXPERIENCE,
			attributes: { action: mutationType },
			error: mutationError,
		});
	}

	void flags.showErrorFlag({
		title: formatMessage(getErrorTitleM1(mutationType, mutationUpdateReason)),
		description: formatMessage(i18nM1.errorFlagDescription),
	});

	createAnalyticsEvent(
		getMutationAnalyticsEventPayload({
			mutationType,
			updateReason: isMutationTypeCreate ? undefined : mutationUpdateReason,
			mutationStatus: MutationStatus.ERROR,
			data: convertedError?.message || mutationError.message,
		}),
	).fire();

	if (isExpectedError && shouldMarkErrorAsHandled(mutationError as Error)) {
		markErrorAsHandled(mutationError);
	}
};

export const handleUnexpectedMutationError = ({
	createAnalyticsEvent,
	experienceTracker,
	flags,
	formatMessage,
	mutationUpdateReason,
	mutationSource,
	unexpectedError,
}: HandleUnexpectedMutationErrorsProps) => {
	const mutationType = mutationTypeMapM2[mutationUpdateReason];
	const isMutationTypeCreate = mutationType === MutationType.CREATE;

	const mutationError = {
		message: unexpectedError?.message || `${mutationType} banner error`,
		name: `${mutationType} banner mutation failed`,
	};

	void flags.showErrorFlag({
		title: formatMessage(getGenericTitleError(mutationUpdateReason)),
		description: formatMessage(i18nM1.errorFlagDescription),
	});

	if (mutationUpdateReason === MutationUpdateReason.ARCHIVE) {
		experienceTracker.stopOnError({
			name: ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
			attributes: { source: mutationSource },
			error: mutationError,
		});
	} else {
		experienceTracker.stopOnError({
			name: ADMIN_ANNOUNCEMENT_BANNER_PUBLISH_EXPERIENCE,
			attributes: { action: mutationType },
			error: mutationError,
		});
	}

	createAnalyticsEvent(
		getMutationAnalyticsEventPayload({
			mutationType,
			updateReason: isMutationTypeCreate ? undefined : mutationUpdateReason,
			mutationStatus: MutationStatus.ERROR,
		}),
	).fire();

	if (shouldMarkErrorAsHandled(mutationError as Error)) {
		markErrorAsHandled(mutationError);
	}
};

export const handleErrorsM2 = ({
	createAnalyticsEvent,
	experienceTracker,
	flags,
	formatMessage,
	data,
	isExpectedError = false,
	mutationUpdateReason,
	mutationSource,
	schedulingErrorDetails,
}: HandleErrorsM2Props) => {
	const mutationType = mutationTypeMapM2[mutationUpdateReason];
	const isMutationTypeCreate = mutationType === MutationType.CREATE;
	const mutationData = getProperlyTypedData(isMutationTypeCreate, data);
	const convertedError = mutationData?.errors?.[0];

	const mutationError = {
		message: convertedError?.message || `${mutationTypeMap[mutationType]} banner error`,
		name: `${mutationTypeMap[mutationType]} banner mutation failed`,
	};

	if (!isExpectedError) {
		if (mutationUpdateReason === MutationUpdateReason.ARCHIVE) {
			experienceTracker.stopOnError({
				name: ADMIN_ANNOUNCEMENT_BANNER_TURN_OFF_EXPERIENCE,
				attributes: { source: mutationSource },
				error: mutationError,
			});
		} else {
			experienceTracker.stopOnError({
				name: ADMIN_ANNOUNCEMENT_BANNER_PUBLISH_EXPERIENCE,
				attributes: { action: mutationType },
				error: mutationError,
			});
		}
	}

	const bannerTitle =
		schedulingErrorDetails?.submittedBannerTitle ||
		mutationData?.adminAnnouncementBannerSetting?.title ||
		formatMessage(sharedI18n.untitledBannerTitle);

	const flagTitle = formatMessage(
		...getErrorTitleM2(bannerTitle, mutationUpdateReason, schedulingErrorDetails),
	);
	const flagDescription = formatMessage(...getErrorDescription(schedulingErrorDetails));

	void flags.showErrorFlag({
		title: flagTitle,
		description: flagDescription,
	});

	createAnalyticsEvent(
		getMutationAnalyticsEventPayload({
			mutationType,
			updateReason: isMutationTypeCreate ? undefined : mutationUpdateReason,
			mutationStatus: MutationStatus.ERROR,
			data: mutationError.message,
		}),
	).fire();

	if (isExpectedError || shouldMarkErrorAsHandled(mutationError as Error)) {
		markErrorAsHandled(mutationError);
	}
};
